import { ArrowRightIcon, Braces, Download, FileUp, PlusIcon, ScrollText, Trash2Icon, Upload } from 'lucide-react';
import {
  ButtonWizard,
  HeadingWizard,
  ConfirmationDialog,
  GoToPreviousClaimPageLink,
  GlobalLoader,
} from '../..';
import { CardWizard, SideSheet, SideSheetHeader } from '../../../layouts';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AnimatePresence } from 'framer-motion';
import {
  IExchangeRateForm,
  IExchangeRateTable,
} from '../../../pages/TravelRequest/constants/travelreq.interface';
import { DEFAULT_EXCHANGE_RATE_MAPPING_FORM_VALUES } from '../../../pages/TravelRequest/constants/travelreq.constants';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { useReactTable } from '@tanstack/react-table';
import {
  extractErrorMessageFromAPI,
  formatDayJsDate,
  mapToDropdownOptions,
  BusinessMappingFilterStorageKeys,
  getSavedFilters,
  saveFilters,
} from '../../../utils';
import {
  useCreateExchangeRateMutation,
  useDeleteExchangeRatesMutation,
  useGetCurrenciesQuery,
  useGetExchangeRateSyncLogsQuery,
  useGetExchangeRatesQuery,
  useLazyDownloadSampleExchangeRateQuery,
  useUpdateExchangeRateMutation,
  useUploadExchangeRateMutation,
} from '../../../store/rtkQuery/travelReq';
import { useNotification } from '../../../contexts';
import CommonTravelTable from './CommonTravelTable';
import { Checkbox, Empty, Modal, Pagination, Select, Spin, Tooltip } from 'antd';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import ExchangeRateForm from './ExchangeRateForm';
import DateRangeQuickFilter from '../../DateRangeQuickFilter';
import { useNavigate } from 'react-router-dom';
const columnHelper = createColumnHelper<IExchangeRateTable>();

export default function ExchangeRateMapping() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const defaultFilters = {
    page: 1,
    pageSize: 25,
    sourceCurrencyId: null as number | null,
    targetCurrencyId: null as number | null,
    fromDate: null as string | null,
    toDate: null as string | null,
  };
  const [appliedFilters, setAppliedFilters] = useState(() =>
    getSavedFilters(BusinessMappingFilterStorageKeys.EXCHANGE_RATE, defaultFilters),
  );
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const currencyResponse = useGetCurrenciesQuery({});
  const [exchangeRateShow, setExchangeRateShow] =
    useState(false);

  const exchangeRateForm = useForm<IExchangeRateForm>({
    defaultValues: DEFAULT_EXCHANGE_RATE_MAPPING_FORM_VALUES,
  });

  const [createExchangeRate, createExchangeRateResponse] =
    useCreateExchangeRateMutation();

  const exchangeRateResponse =
    useGetExchangeRatesQuery(appliedFilters);

  const [updateExchangeRate, updateExchangeRateResponse] =
    useUpdateExchangeRateMutation();
  const [deleteExchangeRate, deleteExchangeRateResponse] =
    useDeleteExchangeRatesMutation();

   const [triggerDownload, { isFetching: isDownloadLoading }] = useLazyDownloadSampleExchangeRateQuery();
   const [uploadExchangeRate, { isLoading: isUploadingCSV }] =
      useUploadExchangeRateMutation();
  const [total, setTotal] = useState(0);
  const [showCSV, setShowCSV] = useState(false);
  const [logSheetShow, setLogSheetShow] = useState(false);
  const [logFilters, setLogFilters] = useState({
    page: 1,
    pageSize: 25,
  });
  const [logTotal, setLogTotal] = useState(0);
  const [jsonModal, setJsonModal] = useState<{
    open: boolean;
    title: string;
    data: any;
  }>({
    open: false,
    title: '',
    data: null,
  });

  const exchangeRateLogResponse = useGetExchangeRateSyncLogsQuery(logFilters, {
    skip: !logSheetShow,
  });

  useEffect(() => {
    if (exchangeRateResponse.data) {
      if (appliedFilters.page === 1) {
        setTotal(exchangeRateResponse.data?.data?.count ?? 0);
      }
    }
  }, [exchangeRateResponse.data]);

  useEffect(() => {
    saveFilters(BusinessMappingFilterStorageKeys.EXCHANGE_RATE, appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    if (exchangeRateLogResponse.data && logFilters.page === 1) {
      setLogTotal(exchangeRateLogResponse.data?.data?.count ?? 0);
    }
  }, [exchangeRateLogResponse.data, logFilters.page]);

    useEffect(() => {
      if (currencyResponse.data?.data && !currencyResponse.isFetching) {
        const defaultCurrencyOption = currencyResponse.data?.data?.find(
          (item: { ShortCode: string }) =>
            item.ShortCode === import.meta.env.VITE_DEFAULT_CURRENCY_SHORTCODE ||
            'INR',
        );
        if (defaultCurrencyOption) {
          exchangeRateForm.setValue('sourceCurrencyId', defaultCurrencyOption?.ID);
          exchangeRateForm.setValue('targetCurrencyId', defaultCurrencyOption?.ID);
        }
      }
    }, [currencyResponse.data]);

    const currencyDropdown = useMemo(() => {
      if (currencyResponse.data) {
        return mapToDropdownOptions(
          currencyResponse.data?.data || [],
          ['ShortCode', 'CurrencyName'],
          'ID',
        );
      }
      return [];
    }, [currencyResponse.data]);

  const onSubmit = async (data: IExchangeRateForm) => {
    try {
      const payload = cleanPayload({
        sourceCurrencyId: data.sourceCurrencyId,
        targetCurrencyId: data.targetCurrencyId,
        ratePerUnitInTarget: data.ratePerUnitInTarget,
        rateDate: data.rateDate,
      });

      if (Object.keys(payload).length === 0) {
        return showNotification(
          'warning',
          'Warning',
          'Please fill at least one field.',
        );
      }

      if (editIndex !== null) {
        if (!data.id) {
          return showNotification(
            'error',
            'Error',
            'ID is required for updating Exchange Rate Mapping.',
          );
        }

        await updateExchangeRate({ id: data.id, ...payload }).unwrap();
        showNotification(
          'success',
          'Success',
          'Exchange Rate Mapping updated successfully.',
        );
      } else {
        await createExchangeRate(payload).unwrap();
        showNotification(
          'success',
          'Success',
          'Exchange Rate Mapping created successfully.',
        );
      }

      // Reset after submission
      setExchangeRateShow(false);
      setEditIndex(null);
      exchangeRateForm.reset(
        DEFAULT_EXCHANGE_RATE_MAPPING_FORM_VALUES,
      );
    } catch (err) {
      console.error(err);
      showNotification(
        'error',
        'Error',
        'Failed to save Exchange Rate Mapping. Please try again.',
      );
    }
  };

  const onEdit = (index: number) => {
    const entry = table.getRowModel().rows[index]?.original;
    if (!entry) return;

    exchangeRateForm.reset(entry);
    setEditIndex(index);
    setExchangeRateShow(true);
  };

  const cleanPayload = (payload: any) => {
    return Object.fromEntries(
      Object.entries(payload).filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
  };

  const onDeleteConfirm = async () => {
    try {
      const idsToDelete = Object.keys(selectedRows).filter(
        id => selectedRows[id],
      );
      if (idsToDelete.length > 0) {
        await deleteExchangeRate(idsToDelete.map(Number)).unwrap();
        showNotification(
          'success',
          'Success',
          'Selected entries deleted successfully.',
        );
        setSelectedRows({});
        setAppliedFilters(prev => ({
          ...prev,
          page: 1,
        }));
        exchangeRateResponse.refetch();
      }
    } catch (error) {
      console.error('Delete Error:', error);
      showNotification('error', 'Error', 'Failed to delete selected entries.');
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  const data = exchangeRateResponse.data?.data?.list || [];

  const allSelected = useMemo(() => {
    return data.length > 0 && data.every((row: any) => selectedRows[row.id]);
  }, [data, selectedRows]);

  const someSelected = useMemo(() => {
    return data.some((row: any) => selectedRows[row.id]);
  }, [data, selectedRows]);

    const [selectedFile, setSelectedFile] = useState(null);
  
   const handleDownloadTemplate = async () => {
    try {
      const blob: any = await triggerDownload(null).unwrap(); // no params needed
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exchange_rate_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };
  
    const handleUploadExcel = async () => {
    
    if (!selectedFile) {
      showNotification("error", "Error", "Please select a file first");
      return;
    }
  
    try {
      const formData = new FormData();
formData.append('file', selectedFile); // 'file' is your backend field name

const response = await uploadExchangeRate(formData).unwrap();
          showNotification(
            'success',
            'Success',
            response?.message || 'Request uploaded successfully',
          );
      } catch (err) {
        showNotification(
          'error',
          'Error',
          extractErrorMessageFromAPI(err) ||
            'Something went wrong . Please try again',
        );
      } finally {
        setSelectedFile(null);
        setShowCSV(false);
      }
    };

  const handleSelectAll = () => {
    const newSelection = allSelected
      ? {}
      : Object.fromEntries(data.map((row: any) => [row.id, true]));
    setSelectedRows(newSelection);
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

const columns = useMemo(
  () => [
    columnHelper.display({
      id: 'select',
      header: () => (
        <Checkbox
          indeterminate={someSelected}
          checked={allSelected}
          onChange={handleSelectAll}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={!!selectedRows[row.original.id]}
          onChange={() => handleSelectRow(row.original.id)}
        />
      ),
      size: 50,
    }),

    // ✅ Source Currency
    columnHelper.accessor('sourceCurrencyId', {
      header: () => 'Source Currency',
      cell: ({ row }) => {
        const sc = row.original?.sourceCurrency;
        if (!sc) return '-';
        return `${sc.CurrencyName} (${sc.ShortCode})`;
      },
    }),

    // ✅ Target Currency
    columnHelper.accessor('targetCurrencyId', {
      header: () => 'Target Currency',
      cell: ({ row }) => {
        const tc = row.original?.targetCurrency;
        if (!tc) return '-';
        return `${tc.CurrencyName} (${tc.ShortCode})`;
      },
    }),

    // ✅ Rate Date
    columnHelper.accessor('rateDate', {
      header: () => 'Rate Date',
      cell: ({ row }) =>
  row.original.rateDate
    ? dayjs(row.original.rateDate).format('DD MMM YYYY')
    : '-',
    }),

    // ✅ Rate Per Unit
    columnHelper.accessor('ratePerUnitInTarget', {
      header: () => 'Rate',
      cell: ({ row }) => row.original.ratePerUnitInTarget || '-',
    }),

    
          columnHelper.accessor('createdAt', {
            header: () => 'Created At',
            cell: ({ row }) =>
              row.original.createdAt
                ? formatDayJsDate(dayjs(row.original.createdAt), 'verbose')
                : '-',
          }),
          columnHelper.accessor('createdBy', {
            header: () => 'Created By',
            cell: ({ row }) => row.original.createdByUser?.FullName || '-',
          }),
          columnHelper.accessor('updatedAt', {
            header: () => 'Updated At',
            cell: ({ row }) =>
              row.original.updatedAt
                ? formatDayJsDate(dayjs(row.original.updatedAt), 'verbose')
                : '-',
          }),
          columnHelper.accessor('updatedBy', {
            header: () => 'Updated By',
            cell: ({ row }) => row.original.updatedByUser?.FullName || '-',
          }),

    // ✅ Action
    columnHelper.accessor('action', {
      header: () => '',
      cell: ({ row }) => (
        
              <Tooltip title="Edit" placement="top">
        <div className="flex gap-1 justify-center">
          <PencilSquareIcon
            onClick={() => onEdit(row.index)}
            className="cursor-pointer size-3.5 stroke-[1px] text-accent-400"
          />
        </div>
        </Tooltip>
      ),
      size: 10,
    }),
  ],
  [selectedRows, allSelected, someSelected],
);

const renderJsonButton = (title: string, data: any) => (
  <Tooltip title="View JSON" placement="top">
    <button
      type="button"
      onClick={() =>
        setJsonModal({
          open: true,
          title,
          data,
        })
      }
      className="inline-flex h-7 w-7 items-center justify-center rounded border border-primary-600 text-primary-600"
    >
      <Braces className="h-4 w-4" />
    </button>
  </Tooltip>
);

const hasJsonContent = (data: any) => {
  if (Array.isArray(data)) return data.length > 0;
  if (data && typeof data === 'object') return Object.keys(data).length > 0;
  return data !== null && data !== undefined && data !== '';
};

  const table = useReactTable<IExchangeRateTable>({
    enableColumnPinning: true,
    columns,
    initialState: {
      columnPinning: {
        left: ['select', 'action'],
      },
    },
    data: exchangeRateResponse.data?.data?.list || [],
    getCoreRowModel: getCoreRowModel(),
  });

  const logData = exchangeRateLogResponse.data?.data?.list || [];

  const mainContent = (
    <>
      {someSelected ? (
        <div className="flex gap-2 pl-4 mb-2">
          <>
            <ButtonWizard
              addOnClass="border border-error-500 mr-auto"
              addOnTextClass="text-error-500"
              buttonText="Delete"
              onBtnClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2Icon className="size-4 stroke-[3px] text-error-500 w-4 h-4" />
            </ButtonWizard>
          </>
        </div>
      ) : (
        <></>
      )}
      <CommonTravelTable
        table={table}
        total={total}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
      />
    </>
  );

  return (
    <div className="p-1.5 flex flex-col gap-2">
      <Spin spinning={deleteExchangeRateResponse.isLoading}>
        <div>
          <CardWizard addOnClass="p-0 pt-2.5">
            <div>
              <div className="flex items-center justify-between px-4 mb-3">
                <HeadingWizard addOnClass="text-[#6E6893] font-extrabold uppercase tracking-wider text-sm flex items-center gap-x-2">
                  <span>Exchange Rate Mapping</span>
                </HeadingWizard>
                <div className="flex items-center justify-end gap-2">
               <div style={{ width: '220px' }}>
  <DateRangeQuickFilter
 value={
  appliedFilters.fromDate || appliedFilters.toDate
    ? {
        startDate: appliedFilters.fromDate
          ? dayjs(appliedFilters.fromDate)
          : null,
        endDate: appliedFilters.toDate
          ? dayjs(appliedFilters.toDate)
          : null,
      }
    : null
}
 onChange={(newRange) => {
  if (!newRange) {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: '',
      toDate: '',
      page: 1,
    }));
    return;
  }

  const { startDate, endDate } = newRange;

  setAppliedFilters((prev) => ({
    ...prev,
    fromDate: startDate
      ? dayjs(startDate).startOf('day').toISOString()
      : '',
    toDate: endDate
      ? dayjs(endDate).endOf('day').toISOString()
      : '',
    page: 1,
  }));
}}
/>
</div>
                   <Select
  className="w-40"
  value={appliedFilters.sourceCurrencyId}
  placeholder="search source currency"
  options={currencyDropdown}
  showSearch
  optionFilterProp="label"
  optionLabelProp="label"
  allowClear
  onChange={(value) =>
    setAppliedFilters(prev => ({
      ...prev,
      sourceCurrencyId: value ?? null,
    }))
  }
/> 
<Select
  className="w-40"
  value={appliedFilters.targetCurrencyId}
  placeholder="search target currency"
  options={currencyDropdown}
  showSearch
  optionFilterProp="label"
  optionLabelProp="label"
  allowClear
  onChange={(value) =>
    setAppliedFilters(prev => ({
      ...prev,
      targetCurrencyId: value ?? null,
    }))
  }
/>

              <ButtonWizard
                addOnClass="flex items-center gap-1 rounded-md border border-primary-600 text-primary-600 transition px-3 py-1.5 text-sm font-medium"
                addOnTextClass="text-primary-600"
                onBtnClick={() => setShowCSV(true)}
              >
              <Tooltip title="Download & Upload Excel" placement="top">
                <FileUp className="w-4 h-4" />
              </Tooltip>
              </ButtonWizard>
                  <ButtonWizard
                    addOnClass="bg-primary-600 h-8 px-4 flex items-center justify-center mb-1"
                    addOnTextClass={''}
                    buttonText="Add"
                    onBtnClick={() => {
                      setExchangeRateShow(true);
                      setEditIndex(null);
                      exchangeRateForm.reset(
                        DEFAULT_EXCHANGE_RATE_MAPPING_FORM_VALUES,
                      );
                    }}
                  >
                    <PlusIcon className="size-4 stroke-[3px] font-semibold text-white" />
                  </ButtonWizard>
                  <ButtonWizard
                    addOnClass="bg-white h-8 px-4 flex items-center justify-center mb-1 border border-primary-600"
                    addOnTextClass={'text-primary-600'}
                    buttonText="Logs"
                    buttonType="button"
                    onBtnClick={() => {
                      setLogSheetShow(true);
                      setLogFilters({
                        page: 1,
                        pageSize: 25,
                      });
                    }}
                  >
                    <ScrollText className="size-4 stroke-[2px] text-primary-600" />
                  </ButtonWizard>
                   <span
        onClick={() => navigate(-1)}
        className={
          'cursor-pointer text-xs px-2 py-2 rounded-md text-[#4649EC] font-semibold hover:opacity-80 hover:underline transition-colors flex items-center gap-1 w-max border border-[#4649EC]'
        }
      >
        <span>Back</span>
        <ArrowRightIcon className="size-3 stroke-[4px] text-[#4649EC]" />
      </span>
                </div>
              </div>
            </div>
            {mainContent}
          </CardWizard>
        </div>
      </Spin>
      <AnimatePresence>
        {exchangeRateShow && (
          <SideSheet
            isOpen={exchangeRateShow}
            addOnClass="w-[50vw] sm:w-[30vw] max-w-unset"
            onClose={() => setExchangeRateShow(false)}
            contentChildren={
              <div className="p-4">
                <ExchangeRateForm
                  form={exchangeRateForm}
                  dropdownOptions={{
                    currency: currencyDropdown,
                  }}
                />
              </div>
            }
            headerChildren={
              <div className="border-b border-gray-100">
                <SideSheetHeader
                  title={
                    editIndex !== null
                      ? 'Update Exchange Rate Mapping'
                      : 'Add New Exchange Rate Mapping'
                  }
                  onCloseClick={() => {
                    if (
                      !createExchangeRateResponse.isLoading ||
                      !updateExchangeRateResponse.isLoading
                    ) {
                      setExchangeRateShow(false);
                    }
                  }}
                />
              </div>
            }
            footerChildren={
              <div className="p-4 border-t">
                <div className="border-gray-100 flex items-center gap-4">
                  <ButtonWizard
                    addOnClass="bg-white border border-primary-600"
                    addOnTextClass={'text-primary-600'}
                    buttonText="Cancel"
                    onBtnClick={() => {
                      exchangeRateForm.reset(
                        DEFAULT_EXCHANGE_RATE_MAPPING_FORM_VALUES,
                      );
                      setExchangeRateShow(false);
                    }}
                    disabled={
                      createExchangeRateResponse.isLoading ||
                      updateExchangeRateResponse.isLoading
                    }
                  ></ButtonWizard>
                  <ButtonWizard
                    addOnClass={'bg-primary-600'}
                    buttonText={editIndex !== null ? 'Update' : 'Save'}
                    onBtnClick={exchangeRateForm.handleSubmit(
                      onSubmit,
                    )}
                    loading={
                      createExchangeRateResponse.isLoading ||
                      updateExchangeRateResponse.isLoading
                    }
                  ></ButtonWizard>
                </div>
              </div>
            }
          />
        )}
        {logSheetShow && (
          <SideSheet
            isOpen={logSheetShow}
            addOnClass="w-[80vw] sm:w-[65vw] max-w-unset"
            onClose={() => setLogSheetShow(false)}
            headerChildren={
              <div className="border-b border-gray-100">
                <SideSheetHeader
                  title="Exchange Rate Sync Logs"
                  onCloseClick={() => setLogSheetShow(false)}
                />
              </div>
            }
            contentChildren={
              <div className="p-4 h-full">
                <Spin spinning={exchangeRateLogResponse.isFetching}>
                  {exchangeRateLogResponse.isError ? (
                    <div className="rounded-md border border-error-100 bg-error-50 p-4 text-xs font-medium text-error-600">
                      Failed to fetch exchange rate sync logs.
                    </div>
                  ) : logData.length === 0 ? (
                    <div className="grid h-[calc(100vh-10rem)] place-items-center rounded-md border border-gray-200">
                      <Empty />
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <div className="relative max-h-[calc(100vh-10rem)] overflow-auto shadow border border-gray-200">
                        <table className="min-w-full text-sm text-gray-800 border-spacing-0">
                          <thead
                            className="sticky top-0 z-[11] shadow-sm border-b border-gray-300"
                            style={{ backgroundColor: '#F4F2FF' }}
                          >
                            <tr>
                              <th className="px-4 py-3 text-sm font-semibold text-left whitespace-nowrap text-[#6E6893]">Name</th>
                              <th className="px-4 py-3 text-sm font-semibold text-left whitespace-nowrap text-[#6E6893]">Start Datetime</th>
                              <th className="px-4 py-3 text-sm font-semibold text-left whitespace-nowrap text-[#6E6893]">End Datetime</th>
                              <th className="px-4 py-3 text-sm font-semibold text-left whitespace-nowrap text-[#6E6893]">Result JSON</th>
                              <th className="px-4 py-3 text-sm font-semibold text-left whitespace-nowrap text-[#6E6893]">Failed JSON</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {logData.map((log: any) => (
                              <tr key={log.id} className="hover:bg-gray-50 transition-colors duration-150">
                                <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-medium border-b border-gray-200">
                                  {log.name || '-'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-medium border-b border-gray-200">
                                  {log.startDatetime
                                    ? formatDayJsDate(dayjs(log.startDatetime), 'verbose')
                                    : '-'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-medium border-b border-gray-200">
                                  {log.endDatetime
                                    ? formatDayJsDate(dayjs(log.endDatetime), 'verbose')
                                    : '-'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-medium border-b border-gray-200">
                                  {hasJsonContent(log.resultJson)
                                    ? renderJsonButton('Result JSON', log.resultJson)
                                    : '-'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-medium border-b border-gray-200">
                                  {hasJsonContent(log.failedEntries)
                                    ? renderJsonButton('Failed Entries', log.failedEntries)
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-between bg-[#F2F0F9] items-center px-4 py-2.5 text-[#6E6893]">
                        <Pagination
                          size="small"
                          total={logTotal}
                          pageSize={logFilters.pageSize}
                          current={logFilters.page}
                          pageSizeOptions={[25, 50, 100, 200]}
                          showSizeChanger
                          showTotal={t => (
                            <span className="text-xs text-[#6E6893] font-semibold tracking-wide">
                              Total {t} items
                            </span>
                          )}
                          onChange={(page, pageSize) =>
                            setLogFilters({
                              page,
                              pageSize,
                            })
                          }
                          onShowSizeChange={(page, pageSize) =>
                            setLogFilters({
                              page,
                              pageSize,
                            })
                          }
                          className="text-[13px] text-[#6E6893]"
                        />
                      </div>
                    </div>
                  )}
                </Spin>
              </div>
            }
          />
        )}
      </AnimatePresence>
      <ConfirmationDialog
        loading={false}
        title="Are you sure?"
        onConfirm={onDeleteConfirm}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isOpen={isDeleteDialogOpen}
      >
        <p className="text-text-200 text-xs">
          Are you sure you want to delete selected entries?
        </p>
      </ConfirmationDialog>
      <GlobalLoader loading={deleteExchangeRateResponse.isLoading} />
      
            {/* request update detail by excel Modals */}
            <Modal
              open={showCSV}
              onCancel={() => setShowCSV(false)}
              footer={null}
              width={640}
              centered
              title="Excel Configuration"
            >
              <Spin spinning={isDownloadLoading || isUploadingCSV}>
                <div className="mt-4 space-y-6">
      
                  <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-xs text-blue-700">
                    Please download the template first and ensure the format remains unchanged before uploading.
                  </div>
                  {/* Simple Download Button */}
                  <div>
                    <button
                      onClick={handleDownloadTemplate}
                      className="w-full flex items-center justify-center gap-2
                   border border-gray-400 text-gray-800
                   hover:bg-gray-50 hover:border-gray-500
                   px-4 py-2 rounded-md text-sm font-medium
                   transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Excel
                    </button>
                  </div>
      
                  {/* Full Width Upload Section */}
                  <div className="space-y-4">
      
                    {/* Dotted Upload Box */}
                    <label className="w-full flex flex-col items-center justify-center 
                                border-2 border-dashed border-gray-300 
                                rounded-md px-4 py-8 cursor-pointer 
                                hover:border-gray-400 transition-colors text-center">
                      <span className="text-sm text-gray-700">
                        Click to select file
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        Supported formats: .xlsx, .xls
                      </span>
      
                      <input
                        type="file"
                        accept=".xlsx, .xls"
                        onChange={(e)=>setSelectedFile(e.target.files[0])}
                        className="hidden"
                      />
                    </label>
      
                    {/* Selected File */}
                    {selectedFile && (
                      <div className="text-xs text-gray-600 truncate">
                        Selected File: {selectedFile.name}
                      </div>
                    )}
      
                    {/* Upload Button */}
                    <button
                      onClick={handleUploadExcel}
                      disabled={!selectedFile}
                      className="flex items-center justify-center gap-2 w-full
                   bg-gray-800 hover:bg-gray-900
                   disabled:bg-gray-300 disabled:cursor-not-allowed
                   px-4 py-2 rounded-md text-sm font-medium text-white"
                    >
                      <Upload className="w-4 h-4" />
                      Upload File
                    </button>
      
                  </div>
      
                </div>
              </Spin>
            </Modal>
            <Modal
              open={jsonModal.open}
              onCancel={() =>
                setJsonModal({
                  open: false,
                  title: '',
                  data: null,
                })
              }
              footer={null}
              width={760}
              centered
              title={jsonModal.title}
            >
              <pre className="max-h-[65vh] overflow-auto rounded-md bg-gray-50 p-4 text-xs text-gray-800">
                {JSON.stringify(jsonModal.data, null, 2)}
              </pre>
            </Modal>
    </div>
  );
}
